<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:file-image',
	name: '$t:blocks.input_file_m2o.name',
	description: '$t:blocks.input_file_m2o.description',
	category: 'input',
	tags: ['form-control'],
	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { UploadId } from '#layers/assets/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { CompositePrimaryKey, GlobalSingleRelationFieldId, MonospaceFile } from '#layers/data-model/public';
import { BlockRenderInputSelectM2O } from 'virtual:monospace-builtin-blocks';
import { computed, ref } from 'vue';
import { useOverlay } from '#imports';
import { FileUploadProgress, useFileUpload, useUploadStore } from '#layers/assets/public';
import { defineBlock, isBlockSerialized, useBlockEditorLibrary } from '#layers/block-engine/public';
import { useM2OState } from '#layers/blocks/composables/relation/use-m2o-state';
import { getItemPrimaryKey } from '#layers/data-model/public';
import { injectFormBlockContext, useM2OPermissions, useRelationFormField } from '#layers/forms/public';
import { LazyCollectionListingDrawer } from '#layers/listing/lazy';

interface InputFileM2OBlockProps extends InputBlockProps {
	fieldId?: GlobalSingleRelationFieldId;
	referencedItemKey?: CompositePrimaryKey;
	showRemove?: boolean;
}

const props = withDefaults(defineProps<InputFileM2OBlockProps>(), {
	// Optional boolean props are cast to `false` when absent (native-attr semantics);
	// default to `true` so the remove button shows unless a field opts out explicitly.
	showRemove: true,
});

;

const { field, referencedItemKey, emitConnect, emitDisconnect, fieldValue, disabled, disabledNotice } = useRelationFormField<'to-one'>(props);
const { item } = useM2OState(field, referencedItemKey, fieldValue);

const formBlockContext = injectFormBlockContext(null);
const permissions = useM2OPermissions({
	field,
	isCreateForm: () => formBlockContext?.isCreate.value ?? false,
});

const { useFilteredBlocks } = useBlockEditorLibrary();

const { filteredBlocks: previewBlocks } = useFilteredBlocks({
	filter: (block) => {
		if (isBlockSerialized(block)) return false;
		if (block.category !== 'file-preview') return false;

		return !!block.tags?.some((tag) => item.value.mediaType?.startsWith(tag));
	},
});
const previewBlock = computed(() => previewBlocks.value[0]);

const showRemove = computed(() => {
	if (props.showRemove === false) return false;
	if (props.readonly) return false;
	if (disabled.value) return false;

	return permissions.canDisconnect.value;
});

const overlay = useOverlay();

async function handleSelect() {
	const modal = overlay.create(LazyCollectionListingDrawer, {
		props: {
			collectionId: field.value!.oppositeCollectionId,
			selection: item.value
				? [getItemPrimaryKey(item.value, field.value!.oppositeCollection!)]
				: [],
			selectionMode: 'single',
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result) {
		if (result.selection.length > 0) {
			emitConnect(result.selection[0]!);
		}
		else if (result.selection.length === 0 && referencedItemKey.value) {
			emitDisconnect(referencedItemKey.value);
		}
	}
}

function handleDeselect() {
	if (referencedItemKey.value) {
		emitDisconnect(referencedItemKey.value);
	}
}

const uploadStore = useUploadStore();
const currentUploadId = ref<UploadId | null>(null);
const currentUpload = computed(() => currentUploadId.value ? uploadStore.getFile(currentUploadId.value) : null);

const { uploadFiles } = useFileUpload({
	onStart: (uploadId) => {
		currentUploadId.value = uploadId;
	},
});

function resetFileUpload() {
	currentUploadId.value = null;
}

function abortUpload() {
	if (!currentUploadId.value) return;

	uploadStore.abortUpload(currentUploadId.value);
	resetFileUpload();
}

async function onFileUpload(file: File) {
	resetFileUpload();

	const [uploadedFile] = await uploadFiles([file]);

	if (uploadedFile) {
		emitConnect(getItemPrimaryKey(uploadedFile, field.value!.oppositeCollection!));
	}
}
</script>

<template>
	<BlockRender
		v-if="item && previewBlock"
		:block="previewBlock.id"
		:props="{
			file: item as MonospaceFile,
			showRemove,
			onRemove: handleDeselect,
		}"
	/>
	<BlockRenderInputSelectM2O
		v-else-if="item"
		:field-id="fieldId!"
		:referenced-item-key="referencedItemKey!"
		:disabled
		:disabled-notice
		:readonly
		:show-remove
	/>
	<FileUploadError v-else-if="currentUpload && currentUpload.status === 'error'" :upload="currentUpload" @remove="resetFileUpload" />
	<FileUploadProgress v-else-if="currentUpload?.status === 'uploading'" :upload="currentUpload" @abort="abortUpload" />
	<FileUpload
		v-else
		:multiple="false"
		browse-existing
		:browse-disabled="!permissions.canConnect.value"
		:upload-disabled="!permissions.canCreate.value"
		:browse-disabled-reason="$t('permissions.no_connect_access')"
		:upload-disabled-reason="$t('permissions.no_create_access')"
		@browse="handleSelect"
		@update:model-value="onFileUpload"
	/>
</template>
